import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import React, { useCallback } from "react";
import SwivuModal from "./SwivuModal.jsx";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import {
  columnAvatarRenderer,
  EntityMainNameDefault,
  getViewStateIconByRecord,
  showConfirmationDialog
} from "../../../other/utils/utils.jsx";
import {MessageDialog} from "@bryntum/schedulerpro";
import { assignViewsState } from "../../../other/utils/otherUtils.jsx";
import {cellMenuItemRemoveRow} from "../../../other/utils/gridCellMenuUtils.jsx";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import { TAGS_KEYS } from "../../../other/constants/TagsKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { nonSupportedViewsForViewState } from "../../../other/constants/OtherKeys.jsx";

class SwivuViewOrganizerEditor extends EditorBase {
  static get $name() {
    return "SwivuViewOrganizerEditor";
  }

  static get type() {
    return WIDGET_KEYS.swivuViewOrganizerEditorPopup;
  }

  static get configurable() {
    return {
      title: "Views Manager",
      autoClose: false,
      draggable: true,
      modal: true,
      closable: true,
      minWidth: "400px",
      width: "35em",
      maxWidth: "100vw",
      minHeight: "600px",
      height: "min(100vh, 600px)",
      maxHeight: "100vh",
      cls: "minimal-gradient-editor editor-header-text-left-aligned swivu-view-organizer",
      stateId: PERSISTENCE_STATE_KEYS.viewOrganizerEditor,
      items: {
        folderNameField: {
          type: "textField",
          label: "Column Name",
          name: "textField",
          placeholder: "Column Name",
          onChange: "up.onFolderNameChange",
        },
        viewOrganizerGrid: {
          height: "100%",
          hideHeaders: true,
          width: "100%",
          type: WIDGET_KEYS.swivuGrid,
          maxHeight: "calc(100% - 2.656em)",
          stateId: PERSISTENCE_STATE_KEYS.viewOrganizerEditorGrid,
          selectionMode: {
            row: true,
            multiSelect: false
          },
          features: {
            filter: false,
            headerMenu: false,
            cellEdit: {
              // when previous field is in editable state and then user call outside somewhere other record column then
              // the other row column gets into the editable state automatically
              editNextOnEnterPress: false,
              continueEditingOnCellClick: false,
            },
            rowReorder: {
              showGrip: true,
            },
            cellMenu: {
              items: {
                copy: false,
                cut: false,
                paste: false,
                removeRow: cellMenuItemRemoveRow({
                  onItem: "up.onRemoveView"
                }),
              }
            }
          },
          listeners: {
            cellDblClick: "up.onGridCellDbClick",
          },
          columns: [
            {
              id: "view-icon",
              text: "Icon",
              field: "iconId",
              renderer({ record, grid }) {
                const displayIconClass = getViewStateIconByRecord(record, grid.parent.context.project);
                return {
                  tag: "i",
                  class: `${displayIconClass} flex basis-1/2 shrink-0 grow-0 justify-center`,
                  style: !record.color ? "": `color: ${record.color}`,
                }
              },
              editor: {
                type: WIDGET_KEYS.fieldIcon,
                onPaint: "up.onPaintFieldIcon"
              },
              cellCls: "!p-0 !min-w-[2.5em] !w-[auto]",
              groupable: false,
            },
            {
              id: "view-name",
              text: 'View Name',
              renderer: ({ value }) => EntityMainNameDefault({text: value}),
              field: "name",
              flex: 1,
              groupable: true,
              width: "10em",
              minWidth: 0,
              editor: false
            },
            {
              id: "view-visible",
              text: "isVisible",
              field: "isVisible",
              type: "check",
              width: "4em",
              groupable: false,
              minWidth: 0
            },
            {
              id: "view-creator",
              text: "Creator",
              field: "creatorUserId",
              renderer: "up.viewAvatarRenderer",
              align: "center",
              width: "4em",
              groupable: false,
              minWidth: 0,
              editor: false
            }
          ]
        }
      }
    };
  }


  loadRecord(record, context, otherParams) {
    if (!otherParams.isFirstShow) return;
    const { viewOrganizerGrid, folderNameField } = this.widgetMap;
    const viewIconColumn = viewOrganizerGrid.columns.getById("view-icon");
    this.changeCrossToEsc();
    super.editRecord(record, context, otherParams);
    this.context = context;
    this.record = record;

    folderNameField.value = record.folderRecord.name;
    viewIconColumn.cellCls = '!w-[8em]';

    // Show views for the specific folder
    viewOrganizerGrid.store.swivuMasterStore = context.project.viewsStore;
    const isAdmin = context.user.can(GRANTS.grant_one);
    folderNameField.disabled = !isAdmin;
    viewOrganizerGrid.store.data = context.project.viewsStore.query((view) => {
      return view.folderId === this.record.folderRecord.id && (isAdmin || view.isDefaultView || view.isShareToHQ || view.isOwnView);
    }, true).sort((a, b) => a.order - b.order);

    assignViewsState([viewOrganizerGrid], context.stateProvider);
    viewIconColumn.editor.store = context.project.resourceTagValues.chain((r) => r.tagSet === TAGS_KEYS.icon)
  }

  onFolderNameChange({ value }) {
    const { folderRecord } = this.record;
    if (folderRecord && this.context.user.can(GRANTS.grant_one)) {
      folderRecord.name = value;
    }
  }

  onGridCellDbClick({ column, record }) {
    if (column.id === 'view-name') {
      if (nonSupportedViewsForViewState.includes(record.viewType)) {
        MessageDialog.alert({
          title: "Unsupported View Type",
          message: "Editing is not available for this view type."
        });
        return false;
      }
      const { editorHandler, viewManager } = this.context;
      const activeView = viewManager.renderState.selectedView;
      const isActiveView = record.id === activeView.id
      editorHandler.openEditor(
        EDITOR_KEYS.viewInspectorEditor,
        null,
        {viewRecord: isActiveView ? activeView : record, canUseRecordViewState: !isActiveView },
        { by: "recordType", useExisting: true}
      )
    }
  }

  onRemoveView() {
    const user = this.context.user;
    const { viewOrganizerGrid } = this.widgetMap;
    const viewToDelete = viewOrganizerGrid.selectedRecord;
    if (viewToDelete) {
      if (viewToDelete.isDefaultView) {
        MessageDialog.alert({ title: "View Delete Not Allowed", message: "Swivu views cannot be deleted." });
      } else if (viewToDelete.isSharedView && (!user.can(GRANTS.grant_one) && viewToDelete.creatorUserId !== user.id)) {
        MessageDialog.alert({ title: "View Share Not Allowed", message: `Shared View created by ${viewToDelete.creatorUserName}. Only creator of this shared view or a board admin can delete this view.` });
      } else {
        showConfirmationDialog({
          title: "Delete View",
          message: `Are you sure, You want to delete a view called "${viewToDelete.name}"?`,
          confirmBtnText: "Delete",
        }).then((isConfirmedDelete) => {
          if (isConfirmedDelete) {
            this.context.viewManager.deleteView(viewToDelete);
            viewOrganizerGrid.store.remove(viewToDelete);
          }
        });
      }
    }
  }

  onPaintFieldIcon({ source }) {
    const iconEl = source.element.querySelector(".icon-label");
    const { record } = source.owner;
    iconEl.style.color = record.color;
  }

  viewAvatarRenderer({ record, size}) {
    if (!record.isGroupHeader) {
      if (record.isDefaultView) {
        return columnAvatarRenderer({
          image: "/assets/Swivu-logo-grey.png",
          name: record.name,
          size: size.configuredHeight / 1.33,
          showName: false,
        })
      } else {
        const creatorBoardUser = this.project.boardUsersStore.find((boardUser) => boardUser.userId === record.creatorUserId);
        if (creatorBoardUser) {
          return columnAvatarRenderer({
            image: creatorBoardUser.profileImageURL,
            name: record.name,
            size: size.configuredHeight / 1.33,
            showName: false,
          })
        }
      }
    }
  }
}

SwivuViewOrganizerEditor.initClass();

const SwivuViewOrganizerEditorPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.loadRecord(record, context, otherParams);
    editorInstance.editorRef = editor;
  }, [context, editorInstance, record]);

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuViewOrganizerEditorPopup}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  )
}

export default SwivuViewOrganizerEditorPopup;
